/**
 * Link de pagamento de venda de canal (C3). Mostra os links já gerados para o pedido e deixa a
 * equipe gerar um novo (só pedido aguardando pagamento; o servidor recusa o resto com 409).
 *
 * O link NÃO confirma pagamento: a confirmação continua vindo da conciliação. Link do simulador
 * (sem credencial do Mercado Pago) é rotulado como fictício e nunca parece um link real.
 *
 * Os links ficam no estado local do painel: o painel recebe `onCreate`/`onLoad` por prop (a tela
 * pai liga o contrato HTTP), então a view continua testável sem provider de API.
 */
// Import explícito de `React` — mesmo motivo de `RmaDetalhe.tsx` (JSX clássico nos testes SSR).
import React, { useEffect, useState, type ReactElement } from "react";
import { Copy, Link2 } from "lucide-react";
import { Badge, Button } from "../ui/index.js";
import { isPaymentLinkUsable, type PaymentLink } from "../contracts/platform-extras.js";
import { formatBrl, formatDateTime } from "../format.js";

export interface PaymentLinkPanelProps {
  orderId: string;
  onLoad?: (orderId: string) => Promise<PaymentLink[]>;
  onCreate?: (orderId: string) => Promise<PaymentLink>;
  /** Relógio injetável (teste/SSR). */
  now?: Date;
}

const ERROR_TEXT: Record<string, string> = {
  order_not_awaiting_payment: "Só é possível cobrar um pedido que ainda aguarda pagamento.",
  order_total_unknown: "O total do pedido ainda não está definido (frete sem regra aprovada): não dá para gerar a cobrança.",
  payment_link_failed: "O provedor de pagamento recusou a criação do link. Tente de novo em instantes."
};

function explain(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  const code = message.split(":")[0] ?? message;
  return ERROR_TEXT[code] ?? `Não foi possível gerar o link: ${message}`;
}

export function PaymentLinkPanel({ orderId, onLoad, onCreate, now = new Date() }: PaymentLinkPanelProps): ReactElement {
  const [links, setLinks] = useState<PaymentLink[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void onLoad?.(orderId).then((loaded) => alive && setLinks(loaded), () => undefined);
    return () => {
      alive = false;
    };
  }, [orderId, onLoad]);

  async function create(): Promise<void> {
    if (!onCreate) return;
    setBusy(true);
    setError(null);
    try {
      const link = await onCreate(orderId);
      setLinks((current) => [link, ...current]);
    } catch (failure) {
      setError(explain(failure));
    } finally {
      setBusy(false);
    }
  }

  async function copy(link: PaymentLink): Promise<void> {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(link.id);
    } catch {
      setCopied(null);
    }
  }

  return (
    <section className="flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1" data-panel="link-pagamento" data-payment-links={links.length}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="m-0 flex items-center gap-2 text-lg font-extrabold text-ink">
          <Link2 className="h-5 w-5 text-brand-text" aria-hidden focusable={false} />
          Link de pagamento
        </h3>
        {onCreate ? (
          <Button variant="primary" disabled={busy} data-action="gerar-link" onClick={() => void create()}>
            {busy ? "Gerando…" : links.length > 0 ? "Gerar novo link" : "Gerar link de pagamento"}
          </Button>
        ) : null}
      </div>
      <p className="m-0 text-xs text-muted">O link cobra o total do pedido e vale por 48 horas. Ele não confirma o pagamento: a confirmação vem da conciliação.</p>
      {error ? <p role="alert" className="m-0 text-sm font-semibold text-danger">{error}</p> : null}
      {links.length === 0 ? (
        <p className="m-0 text-sm text-muted" data-payment-links-empty="true">Nenhum link gerado para este pedido.</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {links.map((link) => {
            const usable = isPaymentLinkUsable(link, now);
            return (
              <li key={link.id} className="flex flex-col gap-1 rounded-control bg-surface-soft px-3 py-2" data-payment-link={link.id}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-bold tabular-nums text-ink">{formatBrl(link.amountCents)}</span>
                  <Badge tone={usable ? "success" : "neutral"}>{usable ? "Ativo" : link.status === "paid" ? "Pago" : "Expirado ou cancelado"}</Badge>
                  {link.provider === "simulator" ? <Badge tone="warning" data-demo-badge="true">Link fictício (simulador)</Badge> : null}
                  <span className="text-xs text-muted">{`válido até ${formatDateTime(link.expiresAt)}`}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <input readOnly aria-label="Endereço do link de pagamento" value={link.url} className="min-w-0 flex-1 rounded-control border border-border bg-surface px-2 py-1 text-xs text-ink" onFocus={(event) => event.currentTarget.select()} />
                  <button type="button" onClick={() => void copy(link)} className="inline-flex items-center gap-1 rounded-control px-2 py-1 text-xs font-bold text-brand-text hover:bg-surface">
                    <Copy className="h-3.5 w-3.5" aria-hidden focusable={false} />
                    {copied === link.id ? "Copiado" : "Copiar"}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
